/**
 * @file    numericSettingsUtils.ts
 * @scope   SHARED
 * @summary Operator options, defaults and validation for the numeric-field advanced settings
 *          (Percentage / Frequency on SimpleInt, SimpleDouble, IntegerQuestion, DoubleQuestion).
 * @consumes FieldModel, SelectedFieldModel, NumericSettingsModel, NUMERIC_TYPES
 * @produces `isNumericField`, `usesNumericSettings`, `NUMERIC_OPERATOR_OPTIONS`,
 *           `isRangeOperator`, `buildDefaultNumericSettings`, `isNumericSettingsComplete`
 * @sideEffects None — pure.
 */
import {
  FieldModel,
  NumericSettingsModel,
  NumericValueOperator,
  SelectedFieldModel,
} from 'models/Enterprise/FieldsManagerModel';
import { NUMERIC_TYPES } from '../../../filters/filterTypeResolver';

export const NUMERIC_OPERATOR_OPTIONS: { value: NumericValueOperator; label: string }[] = [
  { value: 'lessThan', label: '<' },
  { value: 'lessThanOrEqual', label: '<=' },
  { value: 'greaterThan', label: '>' },
  { value: 'greaterThanOrEqual', label: '>=' },
  { value: 'equals', label: '=' },
  { value: 'between', label: 'Between' },
  { value: 'outside', label: 'Outside' },
];

export const isNumericField = (field: FieldModel | undefined): boolean =>
  NUMERIC_TYPES.has(field?.typeName);

/** Edit-mode stubs have no typeName until recovery, so stored numericSettings also count. */
export const usesNumericSettings = (field: SelectedFieldModel): boolean =>
  isNumericField(field.field) || field.numericSettings != null;

export const isRangeOperator = (operator: NumericValueOperator): boolean =>
  operator === 'between' || operator === 'outside';

export const getRequiredValueCount = (operator: NumericValueOperator): number =>
  isRangeOperator(operator) ? 2 : 1;

export const buildDefaultNumericSettings = (): NumericSettingsModel => ({
  valueOperator: 'equals',
  values: [],
});

/** Complete when every required value is a finite number and range bounds are ordered. */
export const isNumericSettingsComplete = (settings: NumericSettingsModel | undefined): boolean => {
  if (!settings) return false;
  const { valueOperator, values } = settings;
  if (values.length !== getRequiredValueCount(valueOperator)) return false;
  if (!values.every((v) => typeof v === 'number' && Number.isFinite(v))) return false;
  return !isRangeOperator(valueOperator) || values[0] < values[1];
};
